import {
  Editor,
  Element,
  Text,
  Node as SlateNode,
  type Path,
  type Descendant,
} from 'slate';
import type Serializer from './htmlSerializers/Serializer';
import MicrosoftPasteShim from './filters/MicrosoftPasteShim';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import normalizeValueJSON from './helpers/normalizeValueJSON';
import wrapNonBlocksInContainer from './helpers/wrapNonBlocksInContainer';
import AhaEditor from '../AhaEditor';
import SerializerText from './htmlSerializers/SerializerText';
import SerializerBlock from './htmlSerializers/SerializerBlock';
import SerializerInfoBlock from './htmlSerializers/SerializerInfoBlock';
import SerializerCodeBlock from './htmlSerializers/SerializerCodeBlock';
import SerializerImage from './htmlSerializers/SerializerImage';
import SerializerVideo from './htmlSerializers/SerializerVideo';
import SerializerAudio from './htmlSerializers/SerializerAudio';
import SerializerInlineMarks from './htmlSerializers/SerializerInlineMarks';
import SerializerCleanup from './htmlSerializers/SerializerCleanup';
import SerializerList from './htmlSerializers/SerializerList';
import SerializerListItem from './htmlSerializers/SerializerListItem';
import SerializerStyle from './htmlSerializers/SerializerStyle';
import SerializerGoogleDocs from './htmlSerializers/SerializerGoogleDocs';
import SerializerEmbed from './htmlSerializers/SerializerEmbed';
import SerializerLink from './htmlSerializers/SerializerLink';
import SerializerAttachment from './htmlSerializers/SerializerAttachment';
import MarkSerializer from './htmlSerializers/MarkSerializer';
import SerializerDividerLine from './htmlSerializers/SerializerDividerLine';
import SerializerCodeSnippet from './htmlSerializers/SerializerCodeSnippet';
import SerializerTableCell from './htmlSerializers/SerializerTableCell';
import SerializerTableRow from './htmlSerializers/SerializerTableRow';
import SerializerTable from './htmlSerializers/SerializerTable';
import SerializerAhaTooltip from './htmlSerializers/SerializerAhaTooltip';
import SerializerAllowedMedia from './htmlSerializers/SerializerAllowedMedia';
import SerializerEmbeddedWhiteboard from './htmlSerializers/SerializerEmbeddedWhiteboard';
import SerializerEmbeddedReport from './htmlSerializers/SerializerEmbeddedReport';
import SerializerSharedSnippet from './htmlSerializers/SerializerSharedSnippet';
import SerializerTranscriptSpeaker from './htmlSerializers/SerializerTranscriptSpeaker';
import SerializerTranscriptCue from './htmlSerializers/SerializerTranscriptCue';
import SerializeEmailVariableReference from './htmlSerializers/SerializerEmailVariableReference';
import SerializerLabelPill from './htmlSerializers/SerializerLabelPill';
import SerializerLinkCard from './htmlSerializers/SerializerLinkCard';
import { type MarkAttributes } from './htmlSerializers/Serializer';
import { isElementNode, isTextNode } from './helpers/serializerHelpers';
import SerializationPlugins from './serializationPlugins';
import SerializerFragment from './htmlSerializers/SerializerFragment';
import type { DocumentValue } from '../types/aha-editor';
import Annotations from '../plugins/AnnotationsPlugin/Annotations';
import SerializerAnnotation from './htmlSerializers/SerializerAnnotation';
import AnnotationsOperationsHandler from '../plugins/AnnotationsPlugin/AnnotationsOperationsHandler';
import MetadataNormalizationPlugin from '../plugins/MetadataNormalizationPlugin/MetadataNormalizationPlugin';
import SerializerAhaReferences from './htmlSerializers/SerializerAhaReferences';

export interface HtmlSerializerOptions {
  tableType?: string;
  tableRowType?: string;
  tableCellType?: string;
  enableFontSizeMark?: boolean;
  enableFontFamilyMark?: boolean;
  preserveWhitespace?: boolean;
  allowedMediaRegex?: RegExp;
  baseURL?: string;
  parseHtml?: (html: string) => HTMLElement;
  // If this is set to true then when handling pastes into the editor the
  // markdown serializer will always accept text content - rather than allowing
  // the default behavior from running.
  alwaysAcceptMarkdown?: boolean;
}

export interface HtmlSerializerContext {
  editor?: Editor;
  treeDepth?: number;
  preDepth?: number;
  inGoogleSheetCell?: boolean;
  styleElements?: Set<Node>;
}

function addKeysToReactArray(children: React.ReactNode[]) {
  return children.map((child, i) => {
    if (child && typeof child === 'object' && 'props' in child) {
      return React.cloneElement(child, { key: i });
    }

    return child;
  });
}

// Convert Slate JSON to and from HTML.
// * Serialize: Slate JSON -> HTML
// * Deserialize: HTML -> Slate JSON
export class HtmlSerializer {
  editor: Editor;
  serializers: (typeof Serializer)[];
  context: HtmlSerializerContext = {};
  options: HtmlSerializerOptions = {};

  // An editor instance is necessary here so that we can know the difference
  // between inline and block elements.
  constructor(
    editor: Editor = HtmlSerializer.createGenericEditor(),
    options: HtmlSerializerOptions = {}
  ) {
    this.editor = editor;

    this.options = {
      tableType: 'table',
      tableRowType: 'table-row',
      tableCellType: 'table-cell',
      enableFontSizeMark: options.enableFontSizeMark,
      enableFontFamilyMark: options.enableFontFamilyMark,
      preserveWhitespace: options.preserveWhitespace,
      allowedMediaRegex: options.allowedMediaRegex,
      baseURL:
        options.baseURL ??
        (typeof window !== 'undefined' ? window.location.origin : undefined),
      parseHtml:
        options.parseHtml ??
        ((html) => new DOMParser().parseFromString(html, 'text/html').body),
    };

    this.serializers = HtmlSerializer.defaultSerializers();
  }

  // Create an editor instance with all known plugins.
  static createGenericEditor(): Editor {
    const ahaEditor = new AhaEditor();
    ahaEditor.register(SerializationPlugins);
    ahaEditor.register(AnnotationsOperationsHandler);
    ahaEditor.register(MetadataNormalizationPlugin);

    ahaEditor.editor.normalize();

    return ahaEditor.editor;
  }

  static defaultSerializers() {
    // The order of the serializers here is very significant.
    // Items at the top of the list run first.
    return [
      SerializerCleanup,
      SerializerAllowedMedia,
      SerializerGoogleDocs,
      SerializerFragment,
      SerializerTable,
      SerializerTableRow,
      SerializerTableCell,
      SerializerAnnotation,
      SerializerInlineMarks,
      SerializerStyle,
      SerializerInfoBlock,
      SerializerCodeBlock,
      SerializerImage,
      SerializerVideo,
      SerializerAudio,
      SerializerList,
      SerializerListItem,
      SerializerEmbed, // Must come before SerializerLink.
      SerializerEmbeddedWhiteboard,
      SerializerEmbeddedReport,
      SerializerLinkCard, // Must come before SerializerLink
      SerializerAhaReferences, // Must come before SerializerLink
      SerializerLink,
      SerializerAttachment,
      SerializerText,
      SerializerDividerLine,
      SerializerCodeSnippet,
      SerializerAhaTooltip,
      SerializerSharedSnippet,
      SerializerTranscriptSpeaker,
      SerializerTranscriptCue,
      SerializeEmailVariableReference,
      SerializerLabelPill,
      SerializerBlock,
    ];
  }

  private applyMarkSerializers(
    node: Text,
    path: Path,
    children: React.ReactNode
  ): React.ReactNode {
    let result: React.ReactNode = children;
    for (const serializer of this.serializers) {
      if (!(serializer.prototype instanceof MarkSerializer)) continue;

      const serialized = serializer.serialize({
        node,
        path,
        children: [children],
        context: this.context,
        options: this.options,
      });
      if (serialized) {
        result = serialized;
        break;
      }
    }
    return result;
  }

  serializeTextNode(node: Text, path: Path, isLast: boolean): React.ReactNode {
    const text = node.text;

    // Special case: if the text is ONLY a newline, treat it as a single BR
    if (text === '\n') {
      const br = <br key={0} />;
      return this.applyMarkSerializers(node, path, br);
    }

    // Convert newlines to <br> tags. If this is the last text in a block and
    // has a trailing newline then add an extra <br> to match the behavior of
    // slate in <TextString>.
    const hasTrailingNewline = text.endsWith('\n');
    let textWithBr: React.ReactNode[] = text
      .split('\n')
      .map((item, index) => (index === 0 ? item : [<br key={index} />, item]));
    if (hasTrailingNewline && isLast) {
      textWithBr.push(<br key={textWithBr.length} />);
    }
    textWithBr = textWithBr.flat();

    return this.applyMarkSerializers(node, path, textWithBr);
  }

  serializeElement(
    node: Descendant,
    path: Path,
    isLeafBlock: boolean,
    isLast: boolean
  ): React.ReactNode {
    if (Text.isText(node)) {
      return this.serializeTextNode(node, path, isLeafBlock && isLast);
    }

    // Track whether we are looking at the last text in an
    if (!isLeafBlock) {
      isLeafBlock =
        Element.isElement(node) &&
        !this.editor.isInline(node) &&
        Editor.hasInlines(this.editor, node);
    }

    let children: React.ReactNode[] = [];
    const childElements = Array.from(SlateNode.children(this.editor, path));

    for (let i = 0; i < childElements.length; i++) {
      const child = childElements[i];
      children.push(
        this.serializeElement(
          child[0],
          child[1],
          isLeafBlock,
          i === childElements.length - 1
        )
      );
    }

    children = addKeysToReactArray(children);

    for (const serializer of this.serializers) {
      const el = serializer.serialize({
        node,
        path,
        children,
        context: this.context,
        options: this.options,
      });
      if (el) {
        return el;
      }
    }
  }

  serialize(options: HtmlSerializerOptions = {}): string {
    this.options = { ...this.options, ...options };
    this.context = { editor: this.editor };

    /* TODO: Do we need to migrate here?
    if (!Value.isValue(valueOrJSON)) {
      valueOrJSON = new ValueMigrator(valueOrJSON).migrate();
    }
    */

    Annotations.markAnnotations(this.editor);

    const elements: React.ReactNode[] = [];
    for (const [child, childPath] of SlateNode.children(this.editor, [])) {
      elements.push(this.serializeElement(child, childPath, false, false));
    }

    const html = renderToStaticMarkup(
      <body>{addKeysToReactArray(elements)}</body>
    );
    return html.slice(6, -7);
  }

  deserializeElement(el: Node, markAttributes: MarkAttributes = {}) {
    if (isTextNode(el)) {
      return SerializerText.deserialize({
        el,
        markAttributes,
        next: (childElements, childAttributes) => [],
        context: this.context,
        options: this.options,
      });
    } else if (!isElementNode(el)) {
      return null;
    }

    const next = (
      childElements: HTMLElement[] | NodeList,
      childAttributes: MarkAttributes
    ): Descendant[] => {
      const children = Array.from(childElements)
        .map((node) => this.deserializeElement(node, childAttributes))
        .filter(Boolean)
        .flat();
      return children;
    };

    // Find a serializer which handles this element.
    for (const serializer of this.serializers) {
      const node = serializer.deserialize({
        el,
        markAttributes,
        next,
        context: this.context,
        options: this.options,
      });
      if (node) {
        return node;
      }
    }

    // If no serializer handled this element, just return its children.
    return next(el.childNodes, markAttributes);
  }

  deserialize(
    html: string | null,
    options: HtmlSerializerOptions = {}
  ): DocumentValue {
    this.options = { ...this.options, ...options };
    this.context = { editor: this.editor };

    if (!html) {
      return {
        children: [
          {
            type: 'paragraph',
            children: [{ text: '' }],
          },
        ],
        metadata: { annotations: {}, tableOfContentsEnabled: false },
      };
    }

    // Cleanup MS word data first.
    html = MicrosoftPasteShim.filter(html);

    if (this.options.parseHtml === undefined) {
      throw new Error('parseHtml option is required');
    }
    // Parse the HTML into DOM nodes, will use JSDOM.fragment when running in faye otherwise DOMParser.
    const childNodes = this.options.parseHtml(html as string).childNodes;

    // Process through the serializer rules.
    let nodes: Descendant[] = Array.from(childNodes)
      .map((el) => this.deserializeElement(el))
      .filter(Boolean)
      .flat();
    // Make sure there is a top level block element.
    if (nodes.length === 0) {
      nodes = [
        {
          type: 'paragraph',
          children: nodes,
        },
      ];
    }

    // Make sure top level blocks are in containers.
    // @ts-expect-error context is set at the top of the method.
    nodes = wrapNonBlocksInContainer(this.context.editor, nodes, 'paragraph');

    // Do simple normalization to improve efficiency.
    nodes = normalizeValueJSON(nodes);

    // Do full normalization using a generic editor instance.
    const editor = HtmlSerializer.createGenericEditor();
    editor.children = nodes;
    editor.metadata = {
      annotations: {},
      tableOfContentsEnabled: false,
    };
    Editor.normalize(editor, { force: true });

    Annotations.unmarkAnnotations(editor);

    return { children: editor.children, metadata: editor.metadata };
  }

  static serializeFragment(fragment: Descendant[]): string {
    const editor = HtmlSerializer.createGenericEditor();
    editor.children = fragment;
    const serializer = new HtmlSerializer(editor);
    return serializer.serialize();
  }
}
